import { useInfiniteQuery } from '@tanstack/react-query';

import { type PresApiSearchProduct } from './aliases';
import { fetchSearchProducts } from './api';
import { SEARCH_RESULTS_STALE_TIME } from './constants';
import {
  createNewSearchId,
  getOrCreateNewSearchId,
} from './getOrCreateNewSearchId';
import {
  buildSearchQueryParams,
  genericMenswearWomenswearGroupsStr,
} from './helpers';
import { useIsMenswearOrWomenswearCategoryPage } from './hooks/useIsMenswearOrWomenswearCategoryPage.ts';
import { buildProductSearchQueryKey } from './queryKeys';
import { type SearchFrom } from './types';
import { type SearchFilters, useSearchFilters } from './useSearchFilters';
import { useSearchInput } from './useSearchInput';

import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { useSessionId } from '@/modules/storage/useSessionId';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function useProductsSearch({
  searchInput,
  searchFilters,
  itemsPerPage,
  from,
  includeBoostedListings = true,
}: {
  searchInput?: string;
  searchFilters?: SearchFilters;
  itemsPerPage?: number;
  from?: SearchFrom;
  includeBoostedListings?: boolean;
} = {}) {
  const { searchInput: defaultSearchInput } = useSearchInput();
  const searchInputVal = searchInput || defaultSearchInput;
  const { searchFilters: defaultSearchFilters } = useSearchFilters();
  const searchFiltersVal = searchFilters || defaultSearchFilters;
  const { location } = useCurrentLocation();
  const [, user] = useCurrentUser();
  const sessionId = useSessionId();

  const optimisticSearchUuid = getOrCreateNewSearchId();

  const isMenswearOrWomenswearCategoryPage =
    useIsMenswearOrWomenswearCategoryPage();
  /**
   * Every search request should have a unique search ID.
   * Whenever the query, filtering or sorting changes, we
   * need to generate a new ID.
   *
   * Full RFC:
   * https://depopmarket.atlassian.net/wiki/spaces/SEARCH/pages/4229955676/Depop-Search-Id
   *
   * Web logic doc:
   * https://depopmarket.atlassian.net/wiki/spaces/WP/pages/4370792609/Search+IDs+on+web+depop-search-id+header
   *
   * To ensure consistent search IDs between requests,
   * and across server and client, we use the search ID
   * from the previous page of results if available,
   * and generate a new one if not.
   *
   * When returning to a previously cached query result, the
   * ID is stored with the page data. If that data is stale,
   * a new request will be made with a new ID.
   */
  return {
    optimisticSearchUuid,
    ...useInfiniteQuery({
      queryKey: buildProductSearchQueryKey({
        location,
        searchInput: searchInputVal,
        searchFilters: searchFiltersVal,
      }),
      queryFn: async ({ pageParam }) => {
        const [cursor, previousPageSearchId] = pageParam.split(':');

        let searchId = previousPageSearchId;
        if (!previousPageSearchId) {
          searchId = optimisticSearchUuid;
        }

        const searchFilters = isMenswearOrWomenswearCategoryPage
          ? { ...searchFiltersVal, groups: genericMenswearWomenswearGroupsStr }
          : searchFiltersVal;

        return {
          searchId,
          ...(await fetchSearchProducts({
            params: buildSearchQueryParams({
              what: searchInputVal,
              searchFilters,
              country: location,
              after: cursor,
              includeBoostedListings,
              user_id: user?.id,
              limit: itemsPerPage,
              from,
            }),
            searchId,
            sessionId,
          }).then((res) => {
            createNewSearchId();
            return res;
          })),
        };
      },
      initialPageParam: `:`, // empty param of the form `{cursor}:{searchId}`
      getNextPageParam: (lastPage) => {
        const { has_more, last } = lastPage.data.page_info ?? {};
        return has_more && last ? `${last}:${lastPage.searchId}` : null;
      },
      select: (data) => ({
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          data: {
            meta: page.data.meta,
            products: page.data.objects as PresApiSearchProduct[],
          },
        })),
      }),
      staleTime: SEARCH_RESULTS_STALE_TIME,
      refetchOnMount: false,
    }),
  };
}
